import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { RTL_LOCALES, type Locale } from '@portfolio/shared';

/**
 * The public site is served in English at "/" and in Arabic at "/ar" (right-to-left). The locale is
 * derived from the URL, so every page has a stable, shareable address in each language. CMS copy
 * arrives already translated (the API is called with ?lang=); the strings below are the fixed UI.
 */

const en = {
  dir: 'ltr' as 'ltr' | 'rtl',
  langName: 'English',
  switchTo: 'العربية',
  switchToLabel: 'اقرأ الموقع بالعربية',
  skipToContent: 'Skip to content',
  mainNav: 'Main',
  footerNav: 'Footer',
  home: 'Home',
  openMenu: 'Open menu',
  closeMenu: 'Close menu',
  themeLabel: (next: 'light' | 'dark'): string =>
    next === 'dark' ? 'Switch to dark mode' : 'Switch to light mode',
  nav: { about: 'About Me', work: 'Work', services: 'Services', insights: 'Insights' },
  bookCall: 'Book a Call',
  /** The footer uses the short form: "About Me" → "About" */
  shortNavLabel: (label: string) => label.replace(/\s+me$/i, ''),
  /** Joins a short list: "Logo & Identity" */
  listJoin: ' & ',
  slideOf: (i: number, n: number) => `${i} of ${n}`,

  keyNumbers: 'Key numbers',
  clientStories: 'Client stories',
  showClientStory: (name: string) => `Show the ${name} story`,
  nextClientStory: 'Next client story',
  readMore: 'Read more',
  showLess: 'Show less',
  chooseTestimonial: 'Choose a testimonial',
  testimonialN: (i: number, name: string) => `Testimonial ${i}: ${name}`,

  work: {
    eyebrow: 'Portfolio',
    title: 'Selected work',
    seoTitle: 'Work',
    loadError: 'Projects could not be loaded right now.',
    emptyCategory: 'No projects in this category yet.',
    empty: 'New work is on its way.',
    viewAll: 'View all work',
    filterLabel: 'Filter projects by category',
    all: 'All',
  },

  project: {
    overview: 'Overview',
    about: (title: string) => `About ${title}`,
    client: 'Client',
    scope: 'Scope',
    year: 'Year',
    status: 'Status',
    link: 'Link',
    visit: 'Visit project',
    story: 'Story',
    testimonial: 'Client testimonial',
    stage: { ACTIVE: 'Active', COMPLETED: 'Completed' },
  },

  newsletter: {
    emailLabel: 'Email address',
    placeholder: 'you@example.com',
    subscribe: 'Subscribe',
    consent: 'I agree to receive the newsletter by email. I can unsubscribe at any time.',
    sent: 'Almost there! Check your inbox and click the confirmation link to complete your subscription.',
    emailRequired: 'Email is required',
    emailInvalid: 'Enter a valid email address',
    consentRequired: 'Please confirm you want to receive the newsletter',
    confirmTitle: 'Confirm subscription',
    missingLink: 'Missing confirmation link',
    missingLinkText: 'Open the link from your confirmation email again.',
    confirming: 'Confirming your subscription…',
    failed: 'That didn’t work',
    backHome: 'Back to the homepage',
    already: 'You are already subscribed',
    subscribed: 'You are subscribed!',
    thanks: 'Thanks for confirming. You can unsubscribe at any time from any email.',
    backSite: 'Back to the website',
    unsubscribeTitle: 'Unsubscribe',
    unsubscribed: 'You have been unsubscribed',
    unsubscribedText: 'You won’t receive any more newsletters. Sorry to see you go!',
    unsubscribeQuestion: 'Unsubscribe from the newsletter?',
    unsubscribeText:
      'You will stop receiving newsletter emails. You can subscribe again at any time.',
    previewLink: 'This link is incomplete or a preview link. Use the link from a real email.',
    unsubscribe: 'Unsubscribe',
  },

  states: {
    loading: 'Loading',
    error: 'Error',
    wrong: 'Something went wrong.',
    couldNotLoad: 'The page could not be loaded.',
    tryAgain: 'Try again',
    notFoundTitle: 'Page not found',
    notFound: 'This page doesn’t exist.',
    notFoundText: 'It may have been moved, unpublished, or the link is mistyped.',
    backHome: 'Back home',
    seeWork: 'See the work',
    broke: 'Something broke on this page',
    brokeText: 'Try reloading. If the problem persists, the error has been logged in the console.',
    reload: 'Reload',
    /** API errors are written in English; Arabic visitors get a generic message instead. */
    apiError: null as string | null,
  },
};

export type Dictionary = typeof en;

const ar: Dictionary = {
  dir: 'rtl',
  langName: 'العربية',
  switchTo: 'English',
  switchToLabel: 'Read the website in English',
  skipToContent: 'انتقل إلى المحتوى',
  mainNav: 'القائمة الرئيسية',
  footerNav: 'روابط التذييل',
  home: 'الرئيسية',
  openMenu: 'افتح القائمة',
  closeMenu: 'أغلق القائمة',
  themeLabel: (next) => (next === 'dark' ? 'التبديل إلى الوضع الداكن' : 'التبديل إلى الوضع الفاتح'),
  nav: { about: 'من أنا', work: 'أعمالي', services: 'خدماتي', insights: 'أسئلة وأجوبة' },
  bookCall: 'احجز مكالمة',
  shortNavLabel: (label) => label,
  listJoin: '، ',
  slideOf: (i, n) => `${i} من ${n}`,

  keyNumbers: 'أرقام',
  clientStories: 'قصص العملاء',
  showClientStory: (name) => `اعرض قصة ${name}`,
  nextClientStory: 'قصة العميل التالية',
  readMore: 'اقرأ المزيد',
  showLess: 'عرض أقل',
  chooseTestimonial: 'اختر شهادة',
  testimonialN: (i, name) => `الشهادة ${i}: ${name}`,

  work: {
    eyebrow: 'معرض الأعمال',
    title: 'أعمال مختارة',
    seoTitle: 'أعمالي',
    loadError: 'تعذّر تحميل المشاريع حاليًا.',
    emptyCategory: 'لا توجد مشاريع في هذا التصنيف بعد.',
    empty: 'أعمال جديدة في الطريق.',
    viewAll: 'كل الأعمال',
    filterLabel: 'تصفية المشاريع حسب التصنيف',
    all: 'الكل',
  },

  project: {
    overview: 'نظرة عامة',
    about: (title) => `عن ${title}`,
    client: 'العميل',
    scope: 'النطاق',
    year: 'السنة',
    status: 'الحالة',
    link: 'الرابط',
    visit: 'زيارة المشروع',
    story: 'القصة',
    testimonial: 'شهادة العميل',
    stage: { ACTIVE: 'جارٍ', COMPLETED: 'مكتمل' },
  },

  newsletter: {
    emailLabel: 'البريد الإلكتروني',
    placeholder: 'you@example.com',
    subscribe: 'اشترك',
    consent: 'أوافق على تلقي النشرة عبر البريد الإلكتروني، ويمكنني إلغاء الاشتراك في أي وقت.',
    sent: 'خطوة أخيرة! افتح بريدك واضغط على رابط التأكيد لإتمام اشتراكك.',
    emailRequired: 'البريد الإلكتروني مطلوب',
    emailInvalid: 'أدخل بريدًا إلكترونيًا صحيحًا',
    consentRequired: 'يرجى تأكيد رغبتك في تلقي النشرة',
    confirmTitle: 'تأكيد الاشتراك',
    missingLink: 'رابط التأكيد غير مكتمل',
    missingLinkText: 'افتح الرابط من رسالة التأكيد مرة أخرى.',
    confirming: 'جارٍ تأكيد اشتراكك…',
    failed: 'لم تنجح العملية',
    backHome: 'العودة إلى الصفحة الرئيسية',
    already: 'أنت مشترك بالفعل',
    subscribed: 'تم اشتراكك!',
    thanks: 'شكرًا على التأكيد. يمكنك إلغاء الاشتراك في أي وقت من أي رسالة.',
    backSite: 'العودة إلى الموقع',
    unsubscribeTitle: 'إلغاء الاشتراك',
    unsubscribed: 'تم إلغاء اشتراكك',
    unsubscribedText: 'لن تصلك أي نشرات بعد الآن. يؤسفنا رحيلك!',
    unsubscribeQuestion: 'هل تريد إلغاء الاشتراك في النشرة؟',
    unsubscribeText: 'ستتوقف رسائل النشرة عن الوصول إليك، ويمكنك الاشتراك مجددًا في أي وقت.',
    previewLink: 'هذا الرابط غير مكتمل أو رابط معاينة. استخدم الرابط من رسالة حقيقية.',
    unsubscribe: 'إلغاء الاشتراك',
  },

  states: {
    loading: 'جارٍ التحميل',
    error: 'خطأ',
    wrong: 'حدث خطأ ما.',
    couldNotLoad: 'تعذّر تحميل الصفحة.',
    tryAgain: 'حاول مجددًا',
    notFoundTitle: 'الصفحة غير موجودة',
    notFound: 'هذه الصفحة غير موجودة.',
    notFoundText: 'ربما نُقلت أو أُلغي نشرها، أو أن الرابط مكتوب بشكل خاطئ.',
    backHome: 'العودة للرئيسية',
    seeWork: 'شاهد الأعمال',
    broke: 'حدث عطل في هذه الصفحة',
    brokeText: 'جرّب إعادة التحميل. إذا استمرت المشكلة فقد سُجّل الخطأ في وحدة التحكم.',
    reload: 'إعادة التحميل',
    apiError: 'تعذّر إتمام الطلب. تحقّق من الرابط أو حاول مجددًا لاحقًا.',
  },
};

const dictionaries: Record<Locale, Dictionary> = { en, ar };

/** Quotes text in its own script's style: «Arabic» or “Latin” (content may fall back to English). */
export const quoteText = (text: string) => (/[؀-ۿ]/.test(text) ? `«${text}»` : `“${text}”`);

const AR_PREFIX = /^\/ar(?=\/|$)/;

export const localeFromPath = (pathname: string): Locale =>
  AR_PREFIX.test(pathname) ? 'ar' : 'en';

/** The path without its language prefix: "/ar/work" → "/work", "/ar" → "/". */
export const stripLocale = (pathname: string) => pathname.replace(AR_PREFIX, '') || '/';

/** A site path in `locale`: "/work" → "/ar/work", "/#about" → "/ar#about". */
export function localizePath(path: string, locale: Locale) {
  if (locale === 'en') return path;
  if (path === '/' || path.startsWith('/#') || path.startsWith('/?')) return `/ar${path.slice(1)}`;
  return `/ar${path}`;
}

export function useLocale() {
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname);
  const t = dictionaries[locale];
  return {
    locale,
    t,
    rtl: RTL_LOCALES.includes(locale),
    /** Prefixes an internal path with the current language. */
    to: (path: string) => localizePath(path, locale),
  };
}

/** Keeps <html lang dir> in sync with the page language (the server sets it on first load). */
export function useDocumentLocale() {
  const { locale, t } = useLocale();
  useEffect(() => {
    const html = document.documentElement;
    html.lang = locale;
    html.dir = t.dir;
    return () => {
      html.lang = 'en';
      html.dir = 'ltr';
    };
  }, [locale, t.dir]);
}
